import { ChangeDetectorRef, Component, Input, OnDestroy, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment as env } from '@env/environment';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { UserService } from '@app/core/services/UserService';
import { LocalStorageService } from '@app/core/local-storage/local-storage.service';
import { TaskCenterNotificationService } from '@app/core/services/TaskCenterNotificationService';
import { OrganizationModalComponent } from '../organization/organization-modal.component';

interface NavigationItem {
  link: string;
  label: string;
  icon: string;
  title?: string;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

@Component({
  standalone: false,
  selector: 'anms-design-test',
  templateUrl: './design-test.component.html',
  styleUrls: ['./design-test.component.scss']
})
export class DesignTestComponent implements OnDestroy {
  private readonly navigationCollapsedStorageKey = 'modernNavigationCollapsed';
  private readonly userUpdatesSubscription: Subscription;
  private readonly taskCenterPendingSubscription?: Subscription;
  @Input() shellOnly = false;
  isNavigationCollapsed = false;
  isMobileNavigationOpen = false;
  isStoppingImpersonation = false;
  homeLink = '/about';
  readonly logo = env.logoUrl || 'assets/logo.png';
  readonly productName = env.appId === 2 ? 'SAMekonomi' : 'SAMpoolen';
  readonly isSamekonomiTheme = env.themeName === 'samfinans-theme';
  readonly year = new Date().getFullYear();
  readonly appId = env.appId;
  navigationGroups: NavigationGroup[] = [];
  secondaryNavigation: NavigationItem[] = [];
  topNavigation: NavigationItem[] = [];
  isAdmin = false;
  isCompanyAdmin = false;
  readonly pendingTaskCenterCount = signal(0);

  constructor(
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private localStorageService: LocalStorageService,
    private taskCenterNotificationService?: TaskCenterNotificationService,
    private changeDetector?: ChangeDetectorRef
  ) {
    this.isNavigationCollapsed = this.localStorageService.getItem(this.navigationCollapsedStorageKey) === true;
    this.userUpdatesSubscription = this.authenticationService.onUserUpdate()
      .subscribe(() => {
        this.configureNavigation();
        this.changeDetector?.markForCheck();
      });
    this.taskCenterPendingSubscription = this.taskCenterNotificationService?.pendingCount$
      .subscribe(count => this.pendingTaskCenterCount.set(this.normalizePendingCount(count)));
    this.configureNavigation();
  }

  ngOnDestroy(): void {
    this.userUpdatesSubscription.unsubscribe();
    this.taskCenterPendingSubscription?.unsubscribe();
  }

  get firstName(): string {
    const user = this.authenticationService.userData && this.authenticationService.userData.User;
    return user && user.FirstName ? user.FirstName : 'välkommen';
  }

  get email(): string {
    const user = this.authenticationService.userData && this.authenticationService.userData.User;
    return user && user.Email ? user.Email : 'hans@example.se';
  }

  get initial(): string {
    return this.firstName.charAt(0).toUpperCase();
  }

  get impersonationState(): any {
    return this.authenticationService.getImpersonationState();
  }

  get canManageEmployees(): boolean {
    return this.appId === 2 && this.isCompanyAdmin;
  }

  get isDashboardRoute(): boolean {
    const routeUrl = (this.router.url || '').split('?')[0];
    return routeUrl === '/about' || routeUrl === '/design-test';
  }

  toggleNavigation(): void {
    this.isNavigationCollapsed = !this.isNavigationCollapsed;
    this.localStorageService.setItem(this.navigationCollapsedStorageKey, this.isNavigationCollapsed);
  }

  openCollapsedNavigation(): void {
    if (this.isNavigationCollapsed) {
      this.toggleNavigation();
    }
  }

  togglePrimaryNavigation(): void {
    if (window.matchMedia('(max-width: 900px)').matches) {
      this.toggleMobileNavigation();
      return;
    }

    this.toggleNavigation();
  }

  toggleMobileNavigation(): void {
    this.isMobileNavigationOpen = !this.isMobileNavigationOpen;
  }

  closeMobileNavigation(): void {
    this.isMobileNavigationOpen = false;
  }

  logout(): void {
    this.authenticationService.logout();
  }

  openEmployeeManagement(): void {
    this.closeMobileNavigation();
    this.dialog.open(OrganizationModalComponent, {
      width: '90%',
      height: '90%',
      data: {
        organization: this.authenticationService.getCurrentUserOrganization()
      }
    });
  }

  stopImpersonation(): void {
    if (this.isStoppingImpersonation) {
      return;
    }

    this.isStoppingImpersonation = true;
    this.authenticationService.stopImpersonation().subscribe({
      next: () => {
        this.isStoppingImpersonation = false;
        this.router.navigate(['/users']);
      },
      error: error => {
        this.isStoppingImpersonation = false;
        const message = error?.error?.detail || 'Det gick inte att återgå till administratörskontot. Logga in igen.';
        this.snackBar.open(message, '', { duration: 7000 });
      }
    });
  }

  private configureNavigation(): void {
    this.homeLink = '/about';
    this.isAdmin = this.userService.userHasRole('admin');
    this.isCompanyAdmin = this.userService.userHasRole('companyadmin');

    if (this.isAdmin) {
      this.homeLink = '/admin/task-center';
      this.configureAdminNavigation();
    } else if (this.appId === 2) {
      this.configureSamEconomyNavigation();
    } else {
      this.configureSamPoolNavigation();
    }

    if (this.isCompanyAdmin && this.authenticationService.userData?.User?.Organization?.IsApiCustomer === true) this.navigationGroups.push({ label: 'Organisation', items: [
      { link: '/organization/api-accounts', label: 'API-konton', icon: 'vpn_key' }
    ] });
    this.topNavigation = this.navigationGroups
      .reduce((items, group) => items.concat(group.items), [] as NavigationItem[])
      .filter(item => item.link !== this.homeLink)
      .slice(0, 6);
  }

  private configureAdminNavigation(): void {
    this.navigationGroups = [
      {
        label: 'Översikt',
        items: [{ link: this.homeLink, label: 'Task Center', icon: 'pending_actions' }]
      },
      {
        label: 'Arbete',
        items: [
          { link: '/mypages/invoices', label: 'Fakturor', icon: 'list_alt' },
          { link: '/users', label: 'Användare', icon: 'people_alt' },
          { link: '/transactions', label: 'Ekonomi', icon: 'attach_money' },
          { link: '/admin/documents', label: 'Medlemsdokument', icon: 'folder_shared' }
        ]
      },
      {
        label: 'Hantera',
        items: [
          { link: '/admin/organizations', label: 'Organisationer', icon: 'group' },
          { link: '/mypages/customers', label: 'Kunder', icon: 'people_alt' },
          { link: '/organization/ipsos-import', label: 'IPSOS filimport', icon: 'upload_file' },
          { link: '/admin/kivra', label: 'Kivra', icon: 'qr_code' },
          { link: '/admin/reports', label: 'Medlemsrapporter', icon: 'fact_check' },
          { link: '/account-balance', label: 'L\u00f6nesaldo', icon: 'chat' },
          { link: '/admin/discount-codes', label: 'Rabattkoder', icon: 'sell' },
          { link: '/admin/payout-reports', label: '\u00c5terrapportering', icon: 'autorenew' }
        ]
      }
    ];

    this.secondaryNavigation = [
      { link: '/admin/task-center', label: 'Utbetalningar', icon: 'pending_actions' },
      { link: '/transactions', label: 'Ekonomi', icon: 'attach_money' },
      { link: '/admin/reports', label: 'Medlemsrapporter', icon: 'fact_check' },
      { link: '/settings', label: 'Inställningar', icon: 'settings' },
      { link: '/mypages/customers', label: 'Kunder', icon: 'people_alt' },
      { link: '/account-balance', label: 'Lönesaldo', icon: 'chat' },
      { link: '/mypages/mydocuments', label: 'Mina dokument', icon: 'attachment' },
      { link: '/admin/discount-codes', label: 'Rabattkoder', icon: 'sell' },
      { link: '/admin/supportchat', label: 'Supportchat', icon: 'chat' },
      { link: '/admin/payout-reports', label: 'Återrapportering', icon: 'autorenew' }
    ];
  }

  private configureSamEconomyNavigation(): void {
    const workItems: NavigationItem[] = this.isCompanyAdmin
      ? [
          { link: '/combined-invoice-tool', label: 'Ny samlingsfaktura', icon: 'library_books' },
          { link: '/organization/invoices', label: 'Fakturor', icon: 'list_alt' },
          { link: '/mypages/mydocuments', label: 'Mina dokument', icon: 'attachment' }
        ]
      : [
          { link: '/invoice-tool', label: 'Ny faktura', icon: 'description' },
          { link: '/mypages/invoices', label: 'Fakturor', icon: 'list_alt' },
          { link: '/mypages/mydocuments', label: 'Mina dokument', icon: 'attachment' }
        ];

    this.navigationGroups = [
      {
        label: 'Översikt',
        items: [{ link: '/about', label: 'Start', icon: 'grid_view' }]
      },
      { label: 'Arbete', items: workItems },
      {
        label: 'Hantera',
        items: [{ link: '/mypages/customers', label: 'Kunder', icon: 'people_alt' }]
      }
    ];

    this.secondaryNavigation = [
      { link: '/settings', label: 'Mina inställningar', icon: 'settings_applications' },
      { link: '/contact', label: 'Kontakt', icon: 'perm_contact_calendar' }
    ];

    if (this.userService.userHasRole('companyuser') && this.userService.userIsParentAccount()) {
      this.secondaryNavigation.push({ link: '/users', label: 'Användare', icon: 'people_alt' });
    }

    if (this.canAccessIpsosImport()) {
      this.navigationGroups[2].items.push({
        link: '/organization/ipsos-import',
        label: 'IPSOS filimport',
        icon: 'upload_file'
      });
    }
  }

  private configureSamPoolNavigation(): void {
    this.navigationGroups = [
      {
        label: 'Översikt',
        items: [{ link: '/about', label: 'Start', icon: 'grid_view' }]
      },
      {
        label: 'Arbete',
        items: [
          { link: '/invoice-tool', label: 'Ny faktura', icon: 'add_circle_outline' },
          { link: '/mypages/invoices', label: 'Fakturor', icon: 'receipt_long' },
          { link: '/transactions', label: 'Ekonomi', icon: 'payments' }
        ]
      },
      {
        label: 'Hantera',
        items: [
          { link: '/mypages/customers', label: 'Kunder', icon: 'groups' },
          { link: '/mypages/mydocuments', label: 'Mina dokument', icon: 'folder_open' },
          { link: '/job-calendar', label: 'Jobbkalender', icon: 'calendar_month' }
        ]
      }
    ];

    this.secondaryNavigation = [
      { link: '/contact', label: 'Kontakt', icon: 'perm_contact_calendar' },
      { link: '/account-balance', label: 'Lönesaldo', icon: 'chat' },
      { link: '/settings', label: 'Mina inställningar', icon: 'settings' },
      { link: '/mypages/customers', label: 'Kunder', icon: 'people_alt' },
      { link: '/job-calendar', label: 'Jobbkalendern', icon: 'event_note' }
    ];

    if (this.canAccessIpsosImport()) {
      this.navigationGroups[2].items.push({
        link: '/organization/ipsos-import',
        label: 'IPSOS filimport',
        icon: 'upload_file'
      });
    }
  }

  private canAccessIpsosImport(): boolean {
    if (this.isAdmin) {
      return true;
    }

    const user = this.authenticationService.userData && this.authenticationService.userData.User;
    if (!user) {
      return false;
    }

    const hasOrganizationAdminAccess = this.isCompanyAdmin ||
      user.OrganizationMasterAccount === true && user.IsParentAccount === true;
    const organization = user.Organization;
    const orgNumber = organization && organization.Company ? organization.Company.OrgNumber : '';
    return !!hasOrganizationAdminAccess && String(orgNumber || '').replace(/\D/g, '') === '5566246954';
  }

  private normalizePendingCount(value: number): number {
    return typeof value === 'number' && isFinite(value) && value > 0 ? Math.floor(value) : 0;
  }
}
